import wretch from 'wretch';

import { type LoadServerCxModuleOptions } from '@cx-modules/client';
import { configureErrorCatchers } from '@cx-modules/errors';
import { getEnvContextualizedCxEndpoint } from '@cx-modules/utils';

import { type LoadMoreArticlesArgs } from '../types';
import { type MoreArticlesResponseData } from '../types/article.types';

// `LoadServerCxModuleRequestParams<T>` was removed from @cx-modules/client by the loadUmdBundle
// cleanup; it resolved to `Omit<T, 'dataApiEndpoint'>` — `dataApiEndpoint` is passed to the fetcher
// separately (see `fetchMoreArticlesData` below), so the request params are the loader args minus
// that field. Inlined here to preserve the original behaviour against the current client.
export type MoreArticlesRequestParams = Omit<LoadMoreArticlesArgs, 'dataApiEndpoint'>;

/**
 * cx-api takes `topics` as a single comma-separated query param, but the template API authors it as an
 * array (`["soccer-serie-a"]`) — and some callers already pass the pre-joined string. Normalise to the
 * wire shape here rather than relying on Array→string coercion inside `URLSearchParams.append`, which
 * also (wrongly) sent `topics=` for an empty array, since `[]` is truthy (NGF-6582).
 *
 * Deliberately NOT applied to `getMoreArticlesCacheKey`: the key is a serialisation contract. Prefetch
 * and hydration always run the same build, so normalising there buys nothing — but it does invalidate
 * every already-captured `dehydratedState`, including the preview fixture, whose baked key holds the raw
 * `topics: []` (apps/atlas-astro/src/preview/fixtures/page.kitchen-sink.US.json). A key that no longer
 * matches turns a cache hit into a live fetch during SSR.
 */
const normaliseTopics = (topics: string | string[] | undefined): string =>
  Array.isArray(topics) ? topics.join(',') : (topics ?? '');

export const fetchMoreArticlesData = async (
  dataApiEndpoint = getEnvContextualizedCxEndpoint(),
  params: MoreArticlesRequestParams,
  options?: LoadServerCxModuleOptions,
): Promise<MoreArticlesResponseData> => {
  const { countryCode, limit, offset, repub, subdivision, topics, type } = params;
  const url = new URL(`${dataApiEndpoint}/v2/articles`);

  if (type) {
    url.searchParams.append('articleType', type);
  }
  if (limit) {
    url.searchParams.append('limit', limit.toString());
  }
  if (offset) {
    url.searchParams.append('offset', offset.toString());
  }
  const normalisedTopics = normaliseTopics(topics);
  if (normalisedTopics) {
    url.searchParams.append('topics', normalisedTopics);
  }

  const client = configureErrorCatchers(
    wretch(url.toString(), {
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        'country-code': countryCode,
        repub,
        ...(subdivision ? { subdivision } : {}),
        ...(options?.nocache && { 'Cache-Control': 'no-cache' }),
      },
    }),
    options,
  );

  return client
    .get()
    .notFound(() => {
      return null;
    })
    .json<MoreArticlesResponseData>();
};

export const getMoreArticlesCacheKey = ({
  countryCode,
  limit,
  repub,
  subdivision,
  topics,
  type,
}: MoreArticlesRequestParams) => ['more-articles', repub, countryCode, subdivision, topics, type, limit];

/**
 * Derived, not hand-written, so the prefetch and the client query can never declare a key shape that has
 * drifted from the one `getMoreArticlesCacheKey` actually builds — a mismatch there is silent at runtime
 * and costs the whole SSR prefetch.
 */
export type MoreArticlesCacheKey = ReturnType<typeof getMoreArticlesCacheKey>;
